import React, { useEffect, useState } from 'react';
import { Crown, CheckCircle, XCircle, Loader2, Infinity as InfinityIcon, Eye, Sparkles } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import api from '../services/api';

const PERKS = [
  { icon: InfinityIcon, text: 'Unlimited likes — no daily cap' },
  { icon: Eye, text: 'See everyone who liked you' },
];

const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : null);

const PlanCard = ({ offer, selected, onSelect }) => (
  <button type="button" onClick={() => onSelect(offer.lookup_key)} data-testid={`plan-${offer.lookup_key}`} aria-pressed={selected}
    className={`relative w-full text-left rounded-xl border-2 px-4 py-3 transition-colors ${selected ? 'border-rose-500 bg-rose-50' : 'border-gray-200 hover:border-rose-300'}`}>
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-semibold text-gray-900">{offer.name}</p>
          {offer.badge && <span className="rounded-full bg-rose-500 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white" data-testid={`plan-badge-${offer.lookup_key}`}>{offer.badge}</span>}
        </div>
        <p className="mt-0.5 text-xs text-gray-500">${offer.per_month.toFixed(2)} per month{offer.savings_pct > 0 ? ` · save ${offer.savings_pct}% vs monthly` : ''}</p>
      </div>
      <p className="shrink-0 text-right text-sm font-semibold text-gray-900">${offer.amount.toFixed(2)}
        <span className="block text-xs font-normal text-gray-500">/ {offer.interval_count > 1 ? `${offer.interval_count} ${offer.interval}s` : offer.interval}</span></p>
    </div>
  </button>
);

// Paid status always comes from the backend (/payments/entitlements); the UI never decides who is paid.
export const PremiumSection = () => {
  const [offers, setOffers] = useState([]);
  const [mode, setMode] = useState('test');
  const [ent, setEnt] = useState(null);
  const [selected, setSelected] = useState('premium_3_month');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const load = () => Promise.all([api.get('/payments/offers'), api.get('/payments/entitlements')])
    .then(([o, e]) => { setOffers(o.data.data.offers); setMode(o.data.data.mode); setEnt(e.data.data); })
    .catch(() => setMsg("Couldn't load Premium options right now."));
  useEffect(() => { load(); }, []);

  const checkout = async () => {
    setBusy(true); setMsg('');
    try {
      const r = await api.post('/payments/checkout', { lookup_key: selected, origin_url: window.location.origin });
      window.location.href = r.data.data.checkout_url;
    } catch (e) { setMsg(e?.response?.data?.detail || 'Could not start checkout. Please try again.'); setBusy(false); }
  };
  const portal = async () => {
    setBusy(true); setMsg('');
    try {
      const r = await api.post('/payments/portal', { origin_url: window.location.origin });
      window.location.href = r.data.data.portal_url;
    } catch (e) { setMsg(e?.response?.data?.detail || 'Could not open the billing portal. Please try again.'); setBusy(false); }
  };
  const action = async (path) => {
    setBusy(true);
    try { const r = await api.post(path); setMsg(r.data.message); await load(); }
    catch (e) { setMsg(e?.response?.data?.detail || 'Something went wrong. Please try again.'); } finally { setBusy(false); }
  };

  if (!ent) return null;
  const premium = ent.tier === 'premium';
  const renew = formatDate(ent.current_period_end);
  return (
    <Card id="premium" className="p-5 space-y-4 scroll-mt-24" data-testid="premium-section">
      <div className="flex items-center gap-2">
        <Crown className={`w-5 h-5 ${premium ? 'text-amber-500' : 'text-gray-400'}`} />
        <h3 className="font-semibold">FlameLink Premium</h3>
        <span className={`ml-auto text-xs font-semibold rounded-full px-2 py-0.5 ${premium ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600'}`} data-testid="premium-tier">
          {premium ? 'PREMIUM' : 'FREE'}
        </span>
      </div>
      {mode === 'test' && (
        <p className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800" data-testid="premium-test-mode-banner">
          Test mode — Premium is not yet on sale. Checkout uses Stripe test cards only; no real money is charged.
        </p>
      )}
      {premium ? (
        <div className="space-y-2" data-testid="premium-status">
          <p className="text-sm text-gray-800"><span className="font-medium">Plan:</span> {ent.plan_name || 'FlameLink Premium'}</p>
          <p className="text-sm text-gray-800"><span className="font-medium">Status:</span> <span data-testid="premium-status-value">{ent.subscription_status}</span>
            {ent.subscription_status === 'past_due' && <span className="text-xs text-orange-600"> · payment issue — please update your card</span>}</p>
          {renew && (
            <p className="text-sm text-gray-800" data-testid="premium-period-end">
              <span className="font-medium">{ent.cancel_at_period_end ? 'Premium ends:' : 'Renews:'}</span> {renew}
            </p>
          )}
          {ent.cancel_at_period_end && <p className="text-xs text-gray-600" data-testid="premium-cancel-note">Cancellation is scheduled. You keep every Premium feature until {renew}; nothing more will be billed.</p>}
          <div className="flex flex-wrap gap-2">
            <Button size="sm" className="bg-rose-500 hover:bg-rose-600 text-white" onClick={portal} disabled={busy} data-testid="premium-manage-btn">Manage subscription</Button>
            {ent.cancel_at_period_end
              ? <Button variant="outline" size="sm" onClick={() => action('/payments/subscription/resume')} disabled={busy} data-testid="premium-resume-btn">Keep my subscription</Button>
              : <Button variant="outline" size="sm" onClick={() => action('/payments/subscription/cancel')} disabled={busy} data-testid="premium-cancel-btn">Cancel subscription</Button>}
          </div>
          <p className="text-[11px] text-gray-500">Manage subscription opens Stripe's secure portal to update your card, view invoices or change billing details.</p>
        </div>
      ) : (
        <>
          <ul className="space-y-1">
            {PERKS.map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-center gap-2 text-sm text-gray-700"><Icon className="w-4 h-4 text-rose-500" />{text}</li>
            ))}
          </ul>
          <div className="grid gap-2 pt-1" data-testid="premium-plan-picker">
            {offers.map((o) => <PlanCard key={o.lookup_key} offer={o} selected={selected === o.lookup_key} onSelect={setSelected} />)}
          </div>
          <Button className="w-full bg-rose-500 hover:bg-rose-600 text-white" onClick={checkout} disabled={busy || !offers.length} data-testid="premium-checkout-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-2" />Continue to secure checkout</>}
          </Button>
          <p className="text-[11px] text-gray-500">Recurring subscription billed by Stripe. Cancel any time; access continues until the end of the paid period.</p>
        </>
      )}
      {msg && <p className="text-xs text-gray-600" data-testid="premium-msg">{msg}</p>}
    </Card>
  );
};

// Landing page after Stripe Checkout: polls the backend until the webhook/status check confirms payment.
export const PaymentReturn = ({ outcome, sessionId, onDone }) => {
  const [state, setState] = useState(outcome === 'cancel' ? 'canceled' : 'checking');
  useEffect(() => {
    if (outcome === 'cancel' || !sessionId) return;
    let tries = 0, stop = false;
    const poll = async () => {
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        const ps = r.data.data.payment_status;
        if (ps === 'paid') return setState('paid');
        if (['failed', 'expired', 'refunded'].includes(ps)) return setState('failed');
      } catch { /* keep polling */ }
      if (!stop && ++tries < 10) setTimeout(poll, 2000); else if (!stop) setState('pending');
    };
    poll();
    return () => { stop = true; };
  }, [outcome, sessionId]);
  const copy = {
    checking: ['Confirming your payment…', 'Please wait while Stripe confirms the transaction.', Loader2],
    paid: ['Payment confirmed', 'FlameLink Premium is now active on your account.', CheckCircle],
    pending: ['Payment pending', "We haven't received confirmation yet. Your access will unlock automatically once Stripe confirms.", Loader2],
    failed: ['Payment not completed', 'The payment did not go through. You have not been charged.', XCircle],
    canceled: ['Checkout canceled', 'No payment was made. You can upgrade any time from Settings.', XCircle],
  }[state];
  const Icon = copy[2];
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-rose-50 via-pink-50 to-orange-50 p-4">
      <Card className="w-full max-w-md p-8 text-center space-y-4" data-testid="payment-return" data-state={state}>
        <Icon className={`w-12 h-12 mx-auto ${state === 'paid' ? 'text-green-500' : state === 'checking' || state === 'pending' ? 'text-rose-400 animate-spin' : 'text-red-400'}`} />
        <h2 className="text-xl font-bold" data-testid="payment-return-title">{copy[0]}</h2>
        <p className="text-sm text-gray-600" data-testid="payment-return-text">{copy[1]}</p>
        <Button className="w-full" onClick={onDone} data-testid="payment-return-continue">Continue to FlameLink</Button>
      </Card>
    </div>
  );
};
